import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Briefcase,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  Phone,
  Plus,
  UserMinus,
  Users,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import apiClient from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import toast from "react-hot-toast";

const inputClass =
  "w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-blue-500 focus:outline-none";

// Accessible Status Visual Badges (Icon + Explicit Label + Color)
const STATUS_CONFIG = {
  accepted: {
    label: "Accepté",
    icon: CheckCircle2,
    badgeClass: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    iconClass: "text-emerald-400",
  },
  in_progress: {
    label: "En cours",
    icon: Clock,
    badgeClass: "bg-blue-500/10 text-blue-300 border-blue-500/30",
    iconClass: "text-blue-400",
  },
  refused: {
    label: "Refusé",
    icon: XCircle,
    badgeClass: "bg-rose-500/10 text-rose-300 border-rose-500/30",
    iconClass: "text-rose-400",
  },
  closed_other_mission: {
    label: "Retenu sur une autre mission",
    icon: AlertTriangle,
    badgeClass: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    iconClass: "text-amber-400",
  },
  abandoned: {
    label: "Sans suite",
    icon: UserMinus,
    badgeClass: "bg-slate-700/40 text-slate-300 border-slate-600/40",
    iconClass: "text-slate-400",
  },
};

export default function RecruitmentClientDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("needs"); // "needs" | "candidates"

  // Add Contact Modal State
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", role: "" });
  const [submittingContact, setSubmittingContact] = useState(false);
  const [contactError, setContactError] = useState("");

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/recruitment/companies/${id}/overview`);
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Échec du chargement de la fiche entreprise.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) void fetchOverview();
  }, [id, fetchOverview]);

  const handleAddContact = async (e) => {
    e.preventDefault();
    setContactError("");
    if (!newContact.name.trim()) {
      setContactError("Le nom de l'interlocuteur est obligatoire.");
      return;
    }
    setSubmittingContact(true);
    try {
      await apiClient.post(`/recruitment/companies/${id}/contacts`, {
        name: newContact.name.trim(),
        email: newContact.email.trim() || undefined,
        phone: newContact.phone.trim() || undefined,
        role: newContact.role.trim() || "Interlocuteur",
      });
      toast.success("Interlocuteur ajouté avec succès !");
      setShowAddContact(false);
      setNewContact({ name: "", email: "", phone: "", role: "" });
      await fetchOverview();
    } catch (err) {
      setContactError(err?.response?.data?.detail || "Échec de l'ajout de l'interlocuteur.");
    } finally {
      setSubmittingContact(false);
    }
  };

  const visibleNeeds = useMemo(() => {
    const rawNeeds = data?.needs || [];
    return rawNeeds
      .filter((n) => {
        if (!n) return false;
        if (n.is_deleted === true) return false;
        if (String(n.status || "").trim().toLowerCase() === "deleted") return false;
        if (n.deleted_at != null && String(n.deleted_at).trim() !== "") return false;
        return true;
      })
      .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
  }, [data?.needs]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-sm">
        {error || "Entreprise introuvable."}
      </div>
    );
  }

  const { company, candidate_presentations = [] } = data;
  const isClient = company.company_type === "client";

  return (
    <div className="space-y-6 pb-16" data-testid="recruitment-client-detail-page">
      {/* Navigation Breadcrumbs */}
      <div className="flex items-center gap-3">
        <Link
          to="/admin/recrutement/clients"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour aux entreprises
        </Link>
        <span className="text-slate-600">/</span>
        <span className="text-xs text-blue-400 font-mono">{company.name}</span>
      </div>

      {/* Header Company Card */}
      <section className="glass rounded-3xl p-6 lg:p-8 border border-white/10 space-y-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span
                className={`px-3 py-0.5 rounded-full text-xs font-semibold border ${
                  isClient
                    ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20"
                    : "bg-amber-500/10 text-amber-300 border-amber-500/20"
                }`}
              >
                {isClient ? "Client Actif (Pilotage)" : "Prospect Recrutement"}
              </span>
            </div>
            <h1 className="text-2xl font-heading font-extrabold text-white tracking-tight flex items-center gap-2.5">
              <Building2 className="h-6 w-6 text-blue-400" />
              {company.name}
            </h1>
          </div>

          <button
            type="button"
            onClick={() => setShowAddContact(true)}
            className="px-3.5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white shadow flex items-center gap-1.5 self-start"
          >
            <Plus className="h-3.5 w-3.5" />
            + Ajouter un interlocuteur
          </button>
        </div>

        {/* Contacts List */}
        <div className="pt-3 border-t border-white/5 space-y-2">
          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block">
            Interlocuteurs rattachés ({company.contacts?.length || 0})
          </span>
          {company.contacts?.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {company.contacts.map((contact, idx) => (
                <div
                  key={contact.id || idx}
                  className="p-3 rounded-xl bg-white/[0.02] border border-white/10 text-xs space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-white">{contact.name}</p>
                    <span className="text-[10px] text-slate-400">{contact.role || "Contact"}</span>
                  </div>
                  {contact.email && (
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                      <Mail className="h-3 w-3 text-slate-500" />
                      <span>{contact.email}</span>
                    </div>
                  )}
                  {contact.phone && (
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                      <Phone className="h-3 w-3 text-slate-500" />
                      <span>{contact.phone}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-500 italic">Aucun interlocuteur spécifique rattaché.</p>
          )}
        </div>
      </section>

      {/* Accessible Status Legend */}
      <div className="glass rounded-2xl p-4 border border-white/10 space-y-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
          Légende accessible des statuts de candidature
        </span>
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(STATUS_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <div
                key={key}
                className={`px-2.5 py-1 rounded-lg border flex items-center gap-1.5 font-semibold ${config.badgeClass}`}
              >
                <Icon className={`h-3.5 w-3.5 ${config.iconClass}`} />
                <span>{config.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* View Selector Tabs */}
      <div className="flex items-center gap-2 p-1 bg-white/[0.03] border border-white/10 rounded-2xl max-w-md">
        <button
          type="button"
          onClick={() => setActiveTab("needs")}
          className={`flex-1 py-2 px-4 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
            activeTab === "needs"
              ? "bg-blue-600 text-white shadow"
              : "text-slate-400 hover:text-white"
          }`}
          data-testid="tab-needs-history"
        >
          <Briefcase className="h-3.5 w-3.5" />
          Historique des besoins ({visibleNeeds.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("candidates")}
          className={`flex-1 py-2 px-4 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
            activeTab === "candidates"
              ? "bg-blue-600 text-white shadow"
              : "text-slate-400 hover:text-white"
          }`}
          data-testid="tab-candidates-presented"
        >
          <Users className="h-3.5 w-3.5" />
          Candidats présentés ({candidate_presentations.length})
        </button>
      </div>

      {/* VIEW 1: Historique des besoins */}
      {activeTab === "needs" && (
        <section className="space-y-3" data-testid="needs-history-section">
          {visibleNeeds.length === 0 ? (
            <div
              className="glass rounded-2xl p-8 border border-white/10 text-center text-xs text-slate-400"
              data-testid="empty-needs-history"
            >
              {isClient
                ? "Aucun besoin enregistré pour ce client."
                : "Aucun besoin enregistré pour ce prospect."}
            </div>
          ) : (
            <div className="grid gap-3" data-testid="needs-history-list">
              {visibleNeeds.map((need) => (
                <Link
                  key={need.id || need._id}
                  to={`/admin/recrutement/besoins/${need.id || need._id}`}
                  className="glass rounded-2xl p-4 border border-white/10 hover:border-white/20 transition-all flex flex-col md:flex-row md:items-center justify-between gap-3 group"
                  data-testid={`need-card-${need.id || need._id}`}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-white group-hover:text-blue-300 transition-colors">
                        {need.title}
                      </h3>
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                        {need.status || "open"}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                      {need.start_date && <span>Démarrage : {need.start_date}</span>}
                      {need.tjm_client && (
                        <span>
                          TJM : {need.tjm_client}€/j ({need.tjm_client_mode === "fixed" ? "Fixe" : "Max"})
                        </span>
                      )}
                      <span>{need.applications_count || 0} candidature(s) positionnée(s)</span>
                    </div>
                  </div>
                  <span className="text-xs text-blue-400 font-semibold group-hover:underline self-end md:self-center">
                    Voir la fiche besoin &rarr;
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {/* VIEW 2: Historique des candidats présentés */}
      {activeTab === "candidates" && (
        <section className="space-y-3">
          {candidate_presentations.length === 0 ? (
            <div className="glass rounded-2xl p-8 border border-white/10 text-center text-xs text-slate-400">
              Aucun candidat n&apos;a encore été présenté pour cette entreprise.
            </div>
          ) : (
            <div className="grid gap-4">
              {candidate_presentations.map((cand) => {
                const globalConfig =
                  STATUS_CONFIG[cand.global_status] || STATUS_CONFIG.in_progress;
                const GlobalIcon = globalConfig.icon;

                return (
                  <div
                    key={cand.candidate_id}
                    className="glass rounded-2xl p-5 border border-white/10 space-y-4"
                  >
                    {/* Candidate Row Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-white/5">
                      <div className="space-y-0.5">
                        <Link
                          to={`/admin/recrutement/candidats/${cand.candidate_id}`}
                          className="text-base font-bold text-white hover:text-blue-300 transition-colors"
                        >
                          {cand.candidate_name}
                        </Link>
                        <p className="text-xs text-slate-400">{cand.candidate_email || "Email non renseigné"}</p>
                      </div>

                      {/* Global Calculated Status */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 font-medium">Statut global :</span>
                        <div
                          className={`px-2.5 py-1 rounded-lg border flex items-center gap-1.5 text-xs font-semibold ${globalConfig.badgeClass}`}
                        >
                          <GlobalIcon className={`h-3.5 w-3.5 ${globalConfig.iconClass}`} />
                          <span>{cand.global_status_label}</span>
                        </div>
                      </div>
                    </div>

                    {/* Associated Needs for this candidate */}
                    <div className="space-y-2">
                      <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                        Besoins associés & statuts détaillés
                      </span>
                      <div className="space-y-2">
                        {cand.presentations.map((pres) => {
                          const statusConfig =
                            STATUS_CONFIG[pres.status] || STATUS_CONFIG.in_progress;
                          const StatusIcon = statusConfig.icon;

                          return (
                            <div
                              key={pres.application_id}
                              className="p-3 rounded-xl bg-white/[0.02] border border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs"
                            >
                              <div className="space-y-1">
                                <Link
                                  to={`/admin/recrutement/besoins/${pres.need_id}`}
                                  className="font-bold text-white hover:text-blue-300 transition-colors"
                                >
                                  {pres.need_title}
                                </Link>
                                <div className="flex flex-wrap gap-3 text-[11px] text-slate-400">
                                  {pres.contact_id && <span>Interlocuteur : {pres.contact_id}</span>}
                                  {pres.presented_at && (
                                    <span>Présenté le : {new Date(pres.presented_at).toLocaleDateString()}</span>
                                  )}
                                </div>
                                {pres.rejection_reason && (
                                  <p className="text-[11px] text-rose-300 font-medium pt-0.5">
                                    Motif de refus : {pres.rejection_reason}
                                  </p>
                                )}
                              </div>

                              <div
                                className={`px-2.5 py-1 rounded-lg border flex items-center gap-1.5 font-semibold shrink-0 self-start md:self-center ${statusConfig.badgeClass}`}
                              >
                                <StatusIcon className={`h-3.5 w-3.5 ${statusConfig.iconClass}`} />
                                <span>{statusConfig.label}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Add Contact Modal */}
      <Dialog open={showAddContact} onOpenChange={(open) => !open && !submittingContact && setShowAddContact(false)}>
        <DialogContent className="max-w-md bg-[#101426] text-white border-white/10 rounded-2xl w-[calc(100%_-_2rem)]">
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-400" />
            Ajouter un interlocuteur
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            Rattaché à {company.name}.
          </DialogDescription>

          <form onSubmit={handleAddContact} className="space-y-3 pt-2">
            {contactError && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-300">
                {contactError}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">Nom & prénom *</label>
              <input
                type="text"
                required
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                className={inputClass}
                placeholder="Ex: Jean Dupont"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">Rôle / Titre</label>
              <input
                type="text"
                value={newContact.role}
                onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                className={inputClass}
                placeholder="Ex: Responsable Recrutement, DSI..."
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Email</label>
                <input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  className={inputClass}
                  placeholder="jean@client.com"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  className={inputClass}
                  placeholder="06 12 34 56 78"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => setShowAddContact(false)}
                disabled={submittingContact}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={submittingContact || !newContact.name.trim()}
                className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white shadow-md flex items-center gap-2 disabled:opacity-50"
              >
                {submittingContact ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Plus className="h-3.5 w-3.5" />
                    Ajouter l&apos;interlocuteur
                  </>
                )}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
